import {
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  type User,
} from 'firebase/auth';
import {
  collection,
  doc,
  type DocumentData,
  type Firestore,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { type Functions, httpsCallable } from 'firebase/functions';

export interface AccountExport {
  exportedAt: string;
  profile: DocumentData | null;
  recipes: DocumentData[];
  weekPlans: DocumentData[];
  groceries: DocumentData[];
}

export interface AccountClientInterface {
  readonly usesPassword: boolean;
  exportData(): Promise<AccountExport>;
  deleteAccount(password?: string): Promise<void>;
}

export default class AccountClient implements AccountClientInterface {
  constructor(
    private readonly firestore: Firestore,
    private readonly functions: Functions,
    private readonly user: User,
  ) {}

  // Signed up with email and password, so asked for the password again
  // before deleting; otherwise asked to sign in with Google again
  get usesPassword(): boolean {
    return this.user.providerData.some(
      ({ providerId }) => providerId === 'password',
    );
  }

  private async owned(name: string): Promise<DocumentData[]> {
    const snapshot = await getDocs(
      query(
        collection(this.firestore, name),
        where('user', '==', this.user.uid),
      ),
    );
    return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
  }

  // Everything this user owns: not the recipes of family members or
  // recipes shared with them
  async exportData(now = new Date()): Promise<AccountExport> {
    const [profile, recipes, weekPlans, groceries] = await Promise.all([
      getDoc(doc(this.firestore, 'users', this.user.uid)),
      this.owned('recipes'),
      this.owned('weekplans'),
      this.owned('groceries'),
    ]);
    return {
      exportedAt: now.toISOString(),
      profile: profile.exists() ? profile.data() : null,
      recipes,
      weekPlans,
      groceries,
    };
  }

  // Deleting needs a recent sign-in (see functions/src/deleteAccount.ts)
  async reauthenticate(password = ''): Promise<void> {
    if (this.usesPassword) {
      await reauthenticateWithCredential(
        this.user,
        EmailAuthProvider.credential(this.user.email ?? '', password),
      );
    } else {
      await reauthenticateWithPopup(this.user, new GoogleAuthProvider());
    }
  }

  async deleteAccount(password?: string): Promise<void> {
    await this.reauthenticate(password);
    await httpsCallable(this.functions, 'deleteAccount')();
  }
}
